import { z } from 'zod';
import { Button } from './Button';
import { FormActions, FormLayout } from './Field';
import { Form, FormField, useZodForm } from './Form';
import { Input } from './Input';
import { Stack } from './Layout';

export const locationSchema = z.object({
  name: z.string().trim().min(1, 'Lot name is required'),
  streetAddress: z.string().trim().min(1, 'Street address is required'),
  city: z.string().trim().min(1, 'City is required'),
  state: z.string().trim().min(1, 'State is required'),
  zipCode: z.string().trim().min(1, 'ZIP code is required'),
});

export type LocationFormValues = z.infer<typeof locationSchema>;

export interface LocationFormProps {
  /** Open filled with an existing lot; blank otherwise. */
  initial?: LocationFormValues;
  onSubmit: (values: LocationFormValues) => void;
  /** Clears the fields, then tells the container. Drawn only with `showCancel`. */
  onCancel?: () => void;
  /** Disables the buttons and relabels the submit while the save runs. */
  pending?: boolean;
  showCancel?: boolean;
  /**
   * A real <form> that submits on Enter, or a plain block for when it sits
   * inside another form and must not nest one.
   */
  asForm?: boolean;
  /** The submit button's label, and its label while pending. */
  submitLabel?: string;
  pendingLabel?: string;
}

const EMPTY: LocationFormValues = {
  name: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
};

/**
 * The fields that describe a storage lot: a name and a postal address. Holds
 * its own draft and hands the container the accepted values.
 *
 *   <LocationForm onSubmit={createLocation} pending={isPending} />
 *
 * Every field is required, so the marker sits on all five and an empty one
 * is named when the form is submitted.
 */
export function LocationForm({
  initial,
  onSubmit,
  onCancel,
  pending = false,
  showCancel = false,
  asForm = true,
  submitLabel = 'Create Location',
  pendingLabel = 'Creating...',
}: LocationFormProps) {
  const form = useZodForm(locationSchema, { defaultValues: initial ?? EMPTY });

  const cancel = () => {
    form.reset(EMPTY);
    onCancel?.();
  };

  const fields = (
    <>
      <FormField form={form} name="name" label="Lot Name">
        {(field) => <Input {...field} placeholder="e.g., Main Impound Yard" />}
      </FormField>

      <FormField form={form} name="streetAddress" label="Street Address">
        {(field) => (
          <Input {...field} placeholder="e.g., 123 Industrial Blvd" />
        )}
      </FormField>

      <FormLayout columns={2} gap="comfortable">
        <FormField form={form} name="city" label="City">
          {(field) => <Input {...field} placeholder="e.g., Los Angeles" />}
        </FormField>
        <FormField form={form} name="state" label="State">
          {(field) => <Input {...field} placeholder="e.g., CA" />}
        </FormField>
      </FormLayout>

      <FormField form={form} name="zipCode" label="ZIP Code">
        {(field) => <Input {...field} placeholder="e.g., 90210" />}
      </FormField>

      <FormActions>
        {showCancel ? (
          <Button
            type="button"
            variant="outline"
            onClick={cancel}
            disabled={pending}
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type={asForm ? 'submit' : 'button'}
          onClick={
            asForm
              ? undefined
              : () => form.handleSubmit((values) => onSubmit(values))()
          }
          disabled={pending}
        >
          {pending ? pendingLabel : submitLabel}
        </Button>
      </FormActions>
    </>
  );

  if (asForm) {
    return (
      <Form form={form} gap={4} onSubmit={onSubmit}>
        {fields}
      </Form>
    );
  }

  return <Stack gap={4}>{fields}</Stack>;
}
